/* eslint-disable no-restricted-syntax */
import { GrantedPackagesQuery, TevoTicketsQuery } from 'gql'

export const QTY_ANY_FILTER_KEY = 'any'
export const QTY_MORE_FILTER_KEY = 'more'
export const QTY_MORE_FILTER_VALUE = 10

export const SORT_PRICE_H_TO_L = '-price'
export const SORT_PRICE_L_TO_H = 'price'

export const calculateQtyValuesFromMinMaxSplit = (minSplit: number, maxSplit: number) => {
  const qtyOptions = new Set<number>()

  for (let index = minSplit; index <= maxSplit; index += 1) {
    qtyOptions.add(index)
  }

  return [...qtyOptions].sort((a, b) => a - b)
}

export const calculateQtyValuesFromEventDetails = ({ packageInfo, tevoTicketGroups }) => {
  let qtyOptions = []

  if (packageInfo?.length) {
    // every value between min and max split for every package
    for (const { minSplit, maxSplit } of packageInfo) {
      qtyOptions = [...qtyOptions, ...calculateQtyValuesFromMinMaxSplit(minSplit, maxSplit)]
    }
  } else if (tevoTicketGroups?.length) {
    // every splits option for every ticket group
    for (const { splits } of tevoTicketGroups) {
      for (const split of splits) {
        qtyOptions.push(split)
      }
    }
  }

  return [...new Set(qtyOptions)].sort((a, b) => a - b)
}

type QtyOptions = Array<{
  label: string
  value: 'any' | 'more' | number
}>

export const generateQtyOptions = (
  values: Array<number>,
  { withMore = true, withAny = true, type = 'ticket' }
) => {
  const options = [] as QtyOptions

  let tenMore = false

  if (withAny) {
    options.push({
      value: QTY_ANY_FILTER_KEY,
      label: 'Any Quantity',
    })
  }

  for (const value of values) {
    if (withMore) {
      if (+value >= QTY_MORE_FILTER_VALUE) {
        tenMore = true
        break
      }
    }

    options.push({
      value: +value,
      label: `${value} ${+value === 1 ? type : `${type}s`}`,
    })
  }

  if (tenMore && withMore) {
    options.push({
      value: 'more',
      label: `${QTY_MORE_FILTER_VALUE}+ ${type}s`,
    })
  }

  return options
}

export const filterTEvoTicketGroupsByQtyFilter = (
  tevoTicketGroups: TevoTicketsQuery['findArtwork']['tevoTicketGroups'],
  qty: string | number
) => {
  let quantity = qty

  const tickets = tevoTicketGroups && Array.isArray(tevoTicketGroups) ? [...tevoTicketGroups] : []

  if (!quantity || quantity === QTY_ANY_FILTER_KEY) {
    return tickets
  }

  if (quantity === QTY_MORE_FILTER_KEY) {
    quantity = QTY_MORE_FILTER_VALUE

    return tickets.filter(({ splits, availableQuantity }) => {
      let includesMore = false

      for (const split in splits) {
        if (+split >= QTY_MORE_FILTER_VALUE) {
          includesMore = true

          break
        }
      }

      return includesMore && +availableQuantity >= +quantity
    })
  }

  return tickets.filter(
    ({ splits, availableQuantity }) => splits.includes(+quantity) && availableQuantity >= +quantity
  )
}

export const filterTEvoTicketGroupsByKeywordsFilter = (
  tevoTicketGroups: TevoTicketsQuery['findArtwork']['tevoTicketGroups'],
  keywords: Array<string>
) => {
  const tickets = tevoTicketGroups && Array.isArray(tevoTicketGroups) ? [...tevoTicketGroups] : []

  if (!keywords || !Array.isArray(keywords) || !keywords.length) {
    return tickets
  }

  return tickets.filter(({ section, row, publicNotes }) => {
    let matches = false

    for (const keyword of keywords) {
      const regexp = new RegExp(`( ${keyword} )`, 'gi')

      if (`${section} ${row} ${publicNotes}`.match(regexp)) {
        matches = true
        break
      }
    }

    return matches
  })
}

export const sortTEvoTicketGroupsByPrice = (
  tevoTicketGroups: TevoTicketsQuery['findArtwork']['tevoTicketGroups'],
  order: typeof SORT_PRICE_H_TO_L | typeof SORT_PRICE_L_TO_H
) => {
  const tickets = Array.isArray(tevoTicketGroups) ? tevoTicketGroups : []

  if (!order) {
    return tickets
  }

  return tickets.sort((a, b) => {
    if (order === 'price') {
      return a.retailPrice - b.retailPrice
    }

    if (order === '-price') {
      return b.retailPrice - a.retailPrice
    }

    return 0
  })
}

export const sortPackagesByPrice = (
  grantedPackages: Array<GrantedPackagesQuery['allPackages']['edges'][0]['node']>,
  order: typeof SORT_PRICE_H_TO_L | typeof SORT_PRICE_L_TO_H
) => {
  const packages = Array.isArray(grantedPackages) ? grantedPackages : []

  if (!order) {
    return packages
  }

  return packages.sort((a, b) => {
    if (order === 'price') {
      return a.price - b.price
    }

    if (order === '-price') {
      return b.price - a.price
    }

    return 0
  })
}

export const filterPackagesByQtyFilter = (
  packages: Array<GrantedPackagesQuery['allPackages']['edges'][0]['node']>,
  qty: string | number
) => {
  if (!qty || qty === QTY_ANY_FILTER_KEY) {
    return packages
  }

  const qtyValue = qty === QTY_MORE_FILTER_KEY ? QTY_MORE_FILTER_VALUE : qty

  return packages.filter(
    ({ minSplit, maxSplit, availableQuantity }) =>
      +minSplit <= +qtyValue && +maxSplit >= +qtyValue && +availableQuantity >= +qtyValue
  )
}

export const qtyValueFromFilterAndSplit = (quantity: string | number, split: Array<number>) => {
  if (quantity === QTY_MORE_FILTER_KEY) {
    return split[split.length - 1]
  }

  if (quantity === QTY_ANY_FILTER_KEY) {
    return split[0]
  }

  return split.includes(+quantity) ? +quantity : split[0]
}
